import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';

const LIST_KEY = (userId) => ['chats', userId];
const DETAIL_KEY = (userId, cid) => ['chat', userId, cid];

export function useChatList(userId) {
  return useInfiniteQuery({
    queryKey: LIST_KEY(userId),
    enabled: !!userId,
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const url = pageParam
        ? `/user/${userId}/chats?limit=20&before=${encodeURIComponent(pageParam)}`
        : `/user/${userId}/chats?limit=20`;
      const res = await api.get(url);
      return res.data;
    },
    getNextPageParam: (last) => last?.nextBefore || null,
    staleTime: 60_000,
  });
}

export function useChatDetail(userId, cid) {
  return useQuery({
    queryKey: DETAIL_KEY(userId, cid),
    enabled: !!userId && !!cid,
    queryFn: async () => {
      const res = await api.get(`/user/${userId}/chats/${cid}`);
      return res.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useDeleteChat(userId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cid) => {
      await api.delete(`/user/${userId}/chats/${cid}`);
      return cid;
    },
    onMutate: async (cid) => {
      await qc.cancelQueries({ queryKey: LIST_KEY(userId) });
      const prev = qc.getQueryData(LIST_KEY(userId));
      if (prev) {
        const next = {
          ...prev,
          pages: prev.pages.map((p) => ({
            ...p,
            conversations: p.conversations.filter((c) => c.id !== cid),
          })),
        };
        qc.setQueryData(LIST_KEY(userId), next);
      }
      return { prev };
    },
    onError: (_err, _cid, ctx) => {
      if (ctx?.prev) qc.setQueryData(LIST_KEY(userId), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY(userId) });
    },
  });
}
